// Persisted "in-flight" job tracking.
// Lives in localStorage so a teacher can close/refresh their tab during a
// 60-120s Claude generation and the lesson still finds them when they return.

const GEN_KEY = "atomicedu_pending_generate";
const REFINE_KEY = "atomicedu_pending_refines"; // { [activityId]: { jobId, startedAt } }
const STAGED_KEY = "atomicedu_pending_staged_build"; // { jobId, startedAt, estimateSeconds, model, numLessons }
const UNIT_TASK_KEY = "atomicedu_pending_unit_task"; // { [unitId]: { jobId, startedAt, estimateSeconds } }

const safeGet = (key) => {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    if (typeof window === "undefined") return;
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_e) {
    /* quota / SSR */
  }
};

// ===== Generate =====
export const getPendingGenerate = () => safeGet(GEN_KEY);

export const setPendingGenerate = (jobId) => {
  if (!jobId) return clearPendingGenerate();
  safeSet(GEN_KEY, { jobId, startedAt: Date.now() });
};

export const clearPendingGenerate = () => safeSet(GEN_KEY, null);

// ===== Staged unit build (Stage B — task → task-aware lessons) =====
// Survives a tab close/refresh: the build completes server-side and its
// inputs live on the job doc, so on return we only need the jobId to
// resume polling — the teacher never re-enters Step 1 / Step 2.
export const getPendingStagedBuild = () => safeGet(STAGED_KEY);

export const setPendingStagedBuild = (jobId, meta = {}) => {
  if (!jobId) return clearPendingStagedBuild();
  safeSet(STAGED_KEY, { jobId, startedAt: Date.now(), ...meta });
};

export const clearPendingStagedBuild = () => safeSet(STAGED_KEY, null);

// ===== In-place unit standalone task (retire-the-bridge) =====
// Keyed by unitId so a reload mid-build resumes the progress card.
export const getPendingUnitTask = (unitId) => (safeGet(UNIT_TASK_KEY) || {})[unitId] || null;

export const setPendingUnitTask = (unitId, jobId, estimateSeconds) => {
  const all = safeGet(UNIT_TASK_KEY) || {};
  if (!jobId) {
    delete all[unitId];
  } else {
    all[unitId] = { jobId, startedAt: Date.now(), estimateSeconds };
  }
  safeSet(UNIT_TASK_KEY, Object.keys(all).length ? all : null);
};

export const clearPendingUnitTask = (unitId) => setPendingUnitTask(unitId, null);

// ===== Refine (per activity) =====
export const getPendingRefines = () => safeGet(REFINE_KEY) || {};

export const getPendingRefine = (activityId) => {
  if (!activityId) return null;
  const all = getPendingRefines();
  return all[activityId] || null;
};

export const setPendingRefine = (activityId, jobId) => {
  if (!activityId || !jobId) return;
  const all = getPendingRefines();
  all[activityId] = { jobId, startedAt: Date.now() };
  safeSet(REFINE_KEY, all);
};

export const clearPendingRefine = (activityId) => {
  if (!activityId) return;
  const all = getPendingRefines();
  if (all[activityId]) {
    delete all[activityId];
    safeSet(REFINE_KEY, Object.keys(all).length ? all : null);
  }
};
